// src/tabs/logements/DirectMultiplierEditor.jsx — WAGON L (CHANTIER 54, LV1)
//
// Éditeur du multiplicateur prix « Direct » (site direct booking FBZ), borné
// 0,5–1,5, exposé dans l'onglet 📋 Info FBZ (section « Multiplicateurs par
// plateforme »).
//
// Contrat d'API (livré en parallèle par la branche rouge — WAGON L rouge) :
//   POST /api/beds24-mapping
//     { action: 'multiplier_list', logement_id }
//       → { multiplier: number|null }   (valeur Direct courante, null = non défini)
//     { action: 'multiplier_set', logement_id, multiplier }
//       → { ok: true, multiplier: number }
//
// Le multiplicateur Direct est DISTINCT des multiplicateurs OTA (priceRules
// Beds24 2-16, read-only ADR-097) : il s'applique uniquement au prix affiché
// sur le site direct booking FBZ, calculé côté FBZ (pas poussé à Beds24).
//
// SCOPE : édition réservée aux sous-locations Maison Zen (lc1/lc2/lc3) — seules
// unités vendables en direct booking concurrent (cf. LogementCard Phase 5.2).
// Sur tout autre logement, le composant n'est pas monté (garde côté parent).
//
// Sécurité UI :
//   - borne stricte 0,5 ≤ m ≤ 1,5 (validation avant save + clamp visuel)
//   - toast d'échec OBLIGATOIRE sur erreur API (anti fire-and-forget silencieux)
//   - pas d'auth client : JWT admin transmis, gating requireAdmin côté serveur

import { useState, useEffect, useCallback } from "react";
import { DS } from "../../lib/ui";
import { supabase } from "../../supabase";

export const DIRECT_MULTIPLIER_MIN = 0.5;
export const DIRECT_MULTIPLIER_MAX = 1.5;
export const DIRECT_MULTIPLIER_DEFAULT = 1.0;

// Valide une saisie utilisateur → { valid, value, error }.
// `value` est le nombre parsé (non clampé) ; `valid` est false hors borne / NaN.
export function validateDirectMultiplier(raw) {
  if (raw === "" || raw === null || raw === undefined) {
    return { valid: false, value: null, error: "Saisir une valeur." };
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return { valid: false, value: null, error: "Valeur numérique invalide." };
  }
  if (n < DIRECT_MULTIPLIER_MIN || n > DIRECT_MULTIPLIER_MAX) {
    return {
      valid: false,
      value: n,
      error: `Doit être entre ${DIRECT_MULTIPLIER_MIN} et ${DIRECT_MULTIPLIER_MAX}.`,
    };
  }
  return { valid: true, value: n, error: null };
}

async function callBeds24Mapping(body) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Pas de session admin active. Reconnecte-toi.");
  const r = await fetch("/api/beds24-mapping", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    throw new Error(json.error || `HTTP ${r.status}`);
  }
  return json;
}

export default function DirectMultiplierEditor({ logementId }) {
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // toast : { type: 'success'|'error', message } | null
  const [toast, setToast] = useState(null);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const json = await callBeds24Mapping({
        action: "multiplier_list",
        logement_id: logementId,
      });
      const m = json.multiplier;
      setDraft(m === null || m === undefined ? "" : String(m));
    } catch (e) {
      setLoadError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [logementId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!logementId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const json = await callBeds24Mapping({
          action: "multiplier_list",
          logement_id: logementId,
        });
        if (cancelled) return;
        const m = json.multiplier;
        setDraft(m === null || m === undefined ? "" : String(m));
      } catch (e) {
        if (!cancelled) setLoadError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logementId]);

  const validation = validateDirectMultiplier(draft);

  async function handleSave() {
    const { valid, value, error } = validateDirectMultiplier(draft);
    if (!valid) {
      setToast({ type: "error", message: error });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      const json = await callBeds24Mapping({
        action: "multiplier_set",
        logement_id: logementId,
        multiplier: value,
      });
      const saved = typeof json.multiplier === "number" ? json.multiplier : value;
      setDraft(String(saved));
      setToast({
        type: "success",
        message: `✅ Multiplicateur Direct enregistré (×${saved}).`,
      });
    } catch (e) {
      // Toast obligatoire sur échec (anti fire-and-forget silencieux).
      setToast({
        type: "error",
        message: `❌ Échec enregistrement : ${e.message || String(e)}`,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="direct-multiplier-editor">
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: DS.navy,
          marginBottom: 4,
        }}
      >
        🖥️ Multiplicateur Direct (site FBZ)
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#666",
          lineHeight: 1.5,
          marginBottom: 10,
        }}
      >
        Ajuste le prix affiché sur le site direct booking FBZ par rapport au prix
        de base ({DIRECT_MULTIPLIER_MIN}–{DIRECT_MULTIPLIER_MAX}). Ex : 0,9 = -10 %
        pour inciter au direct ; 1,0 = prix de base inchangé. Distinct des
        multiplicateurs OTA (gérés dans Beds24, ci-dessous).
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: "#888" }}>⏳ Chargement…</div>
      )}

      {!loading && loadError && (
        <div
          style={{
            padding: "8px 10px",
            background: "#fce8e8",
            color: DS.red,
            borderRadius: 7,
            fontSize: 11,
            lineHeight: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>⚠️ Lecture impossible : {loadError}</span>
          <button
            type="button"
            onClick={loadCurrent}
            style={{
              background: "#fff",
              border: `1px solid ${DS.red}55`,
              color: DS.red,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          >
            <span style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>×</span>
            <input
              type="number"
              step="0.05"
              min={DIRECT_MULTIPLIER_MIN}
              max={DIRECT_MULTIPLIER_MAX}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="ex: 0.9"
              data-testid="direct-multiplier-input"
              style={{
                border: `1px solid ${draft !== "" && !validation.valid ? DS.red : "#dde3ec"}`,
                borderRadius: 7,
                padding: "7px 10px",
                fontSize: 13,
                outline: "none",
                background: "#f8fafd",
                fontFamily: DS.fontBody,
                width: 90,
              }}
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !validation.valid}
              data-testid="direct-multiplier-save"
              style={{
                background: saving || !validation.valid ? "#cbd5e1" : DS.navy,
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "8px 16px",
                fontSize: 12,
                fontWeight: 700,
                cursor: saving || !validation.valid ? "not-allowed" : "pointer",
                fontFamily: DS.fontBody,
              }}
            >
              {saving ? "⏳ Enregistrement…" : "💾 Enregistrer"}
            </button>
          </div>

          {draft !== "" && !validation.valid && (
            <div
              data-testid="direct-multiplier-bound-error"
              style={{
                fontSize: 11,
                color: DS.red,
                marginTop: 6,
                lineHeight: 1.4,
              }}
            >
              {validation.error}
            </div>
          )}
        </>
      )}

      {toast && (
        <div
          data-testid={
            toast.type === "error"
              ? "direct-multiplier-toast-error"
              : "direct-multiplier-toast-success"
          }
          role="status"
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
            background: toast.type === "error" ? "#fce8e8" : "#e8f5e9",
            color: toast.type === "error" ? DS.red : DS.green,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
